import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Lock, PhoneForwarded } from 'lucide-react';
import { fetchKbSources, toggleKbSource } from '../../../api/ivrAgents.js';
import { AddSourceModal } from './AddSourceModal.jsx';
import { SourcePreviewModal } from './SourcePreviewModal.jsx';
import { useTenant } from '../../TenantContext.jsx';

/**
 * SourcesPane — 左欄：每個 KB 下的所有 source 文件
 *   - checkbox：勾 = 啟用；取消 = disabled (vector search 跳過)
 *   - 列檔名（同源 chunk 合併顯示）
 *   - 「+ 新增來源」彈 NotebookLM 風 modal（拖曳/上傳/網站/文字）
 */
export function SourcesPane({ avatar, sendMessage, isStreaming, lastCompletedAt = 0, readOnly = false, flowMode = null }) {
    const { t } = useTranslation('workspace');
    const { hasCS } = useTenant();   // 無 CS 席次 → 知識庫（CS 專屬）鎖定
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [previewSource, setPreviewSource] = useState(null);
    // 送出中的開關（kbId/sourceId）：上一次 PATCH 回來前不能再點。1019 有人 0.5 秒點一下連點 72 次，
    // 每次後端都整包讀 KB 疊爆 heap —— 後端已改成只讀檔名，這裡再擋重複送出。
    const togglingRef = useRef(new Set());
    const [toggling, setToggling] = useState(() => new Set());
    const kbs = avatar?.knowledgeBase || [];
    const kbsKey = kbs.join(',');

    const [loadError, setLoadError] = useState(null);
    const reload = useCallback(async (opts = {}) => {
        if (kbs.length === 0) { setSources([]); setLoadError(null); return; }
        setLoading(true);
        setLoadError(null);
        try {
            const results = await Promise.all(kbs.map((kbId) =>
                fetchKbSources(kbId, { ...opts, avatarId: avatar?.avatarId })
                    .then((r) => ({ ok: true, kbId, sources: (r.sources || []).map((s) => ({ ...s, kbId })) }))
                    .catch((e) => {
                        console.warn('[SourcesPane] fetch failed for', kbId, ':', e.message);
                        return { ok: false, kbId, err: e.message, sources: [] };
                    })
            ));
            const allErr = results.length > 0 && results.every(r => !r.ok);
            const merged = results.flatMap(r => r.sources).filter(s => !s.supersededAt);
            setSources(merged);
            if (merged.length === 0 && allErr) {
                setLoadError(results[0]?.err || t('sources.loadFailed'));
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kbsKey, avatar?.avatarId]);

    // 第一次 mount：用 Redis cache（快）
    useEffect(() => { reload(); }, [reload]);

    // chat turn 結束 → 強制 refresh（cache bust，因為 AI 可能加了新檔）
    useEffect(() => {
        if (lastCompletedAt) reload({ refresh: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCompletedAt]);

    const handleToggle = async (s) => {
        const key = s.kbId + '/' + s.sourceId;
        if (togglingRef.current.has(key)) return;
        togglingRef.current.add(key);
        setToggling(new Set(togglingRef.current));
        const next = !s.disabled;
        setSources((arr) => arr.map((x) =>
            x.kbId === s.kbId && x.sourceId === s.sourceId ? { ...x, disabled: next } : x
        ));
        try {
            await toggleKbSource(s.kbId, s.sourceId, next, avatar?.avatarId);
        } catch (e) {
            setSources((arr) => arr.map((x) =>
                x.kbId === s.kbId && x.sourceId === s.sourceId ? { ...x, disabled: !next } : x
            ));
            alert(t('sources.toggleFailed', { error: e.message }));
        } finally {
            togglingRef.current.delete(key);
            setToggling(new Set(togglingRef.current));
        }
    };

    const handleAddSource = ({ files, url, text }) => {
        if (isStreaming) return;
        if (files?.length) {
            const fileNames = files.map(f => f.name).join('、');
            sendMessage(t('sources.addFilesPrompt', { fileNames }), {
                files,
                displayText: t('sources.addFilesDisplay', { fileNames }),
            });
            return;
        }
        if (url) {
            sendMessage(t('sources.addUrlPrompt', { url }), {
                displayText: t('sources.addUrlDisplay', { url }),
            });
            return;
        }
        if (text) {
            sendMessage(t('sources.addTextPrompt', { text }), {
                displayText: t('sources.addTextDisplay', { text: text.length > 60 ? text.slice(0, 60) + '…' : text }),
            });
        }
    };

    // 純總機（transfer-only）：flow 只有 router → transfer → end，**沒有 knowledge 節點**，
    // 通話時根本不會查知識庫 → 這裡放文件是無效的（晶吉 0731 反映：以為上傳了就會用）。
    // 顯示說明而不是可上傳的空清單，並給「要能回答問題就升級成智慧客服」的出路。
    if (flowMode === 'transfer-only') {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-0 p-6 text-center">
                <PhoneForwarded size={28} strokeWidth={1.4} className="text-ink-300 mb-3" />
                <div className="text-sm font-medium text-ink-700">{t('sources.transferOnlyTitle')}</div>
                <p className="text-xs text-ink-400 mt-1.5 leading-relaxed max-w-[15rem]">{t('sources.transferOnlyDesc')}</p>
            </div>
        );
    }

    // 無 CS 席次：知識庫是智慧客服專屬功能 → 鎖定，不顯示來源清單/新增鈕
    if (!hasCS) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-0 p-6 text-center">
                <Lock size={28} strokeWidth={1.4} className="text-ink-300 mb-3" />
                <div className="text-sm font-medium text-ink-700">{t('sources.csLockedTitle')}</div>
                <p className="text-xs text-ink-400 mt-1.5 leading-relaxed">{t('sources.csLockedDesc')}</p>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col h-full min-h-0">
                <div className="flex-1 overflow-y-auto p-3 space-y-0.5">
                    {loading && sources.length === 0 && (
                        <div className="text-center text-ink-400 text-xs py-4 italic">{t('common:state.loading')}</div>
                    )}
                    {!loading && sources.length === 0 && !loadError && (
                        <div className="text-center text-ink-400 text-sm py-6 italic">
                            {t('sources.empty')}
                        </div>
                    )}
                    {loadError && (
                        <div className="text-xs text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded-md px-2 py-1.5">
                            {t('sources.loadErrorPrefix', { error: loadError })}
                            <button
                                onClick={() => reload({ refresh: true })}
                                className="ml-2 underline text-danger-500 hover:text-danger-500/80"
                            >
                                {t('common:actions.retry')}
                            </button>
                        </div>
                    )}
                    {sources.map((s) => (
                        <SourceRow
                            key={s.kbId + '/' + s.sourceId}
                            source={s}
                            onToggle={() => handleToggle(s)}
                            onPreview={() => setPreviewSource(s)}
                            readOnly={readOnly}
                            busy={toggling.has(s.kbId + '/' + s.sourceId)}
                        />
                    ))}
                </div>

                {!readOnly && (
                    <div className="border-t border-cream-200 p-3">
                        <button
                            onClick={() => setModalOpen(true)}
                            disabled={isStreaming}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                        >
                            <Plus size={14} strokeWidth={2} />
                            {t('sources.addSource')}
                        </button>
                    </div>
                )}
            </div>

            <AddSourceModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleAddSource}
            />

            <SourcePreviewModal
                open={!!previewSource}
                onClose={() => setPreviewSource(null)}
                kbId={previewSource?.kbId}
                sourceId={previewSource?.sourceId}
                fileName={previewSource?.fileName}
                avatarId={avatar?.avatarId}
            />
        </>
    );
}

function SourceRow({ source, onToggle, onPreview, readOnly = false, busy = false }) {
    const { t } = useTranslation('workspace');
    const { fileName, disabled, chunkCount } = source;
    const displayName = stripUploadPrefix(fileName) || t('sources.unnamed');
    return (
        <div
            className={[
                'flex items-center gap-2 px-2 py-1.5 rounded hover:bg-cream-50 group',
                disabled ? 'opacity-50' : '',
            ].join(' ')}
        >
            <input
                type="checkbox"
                checked={!disabled}
                onChange={onToggle}
                disabled={readOnly || busy}
                title={readOnly ? undefined : t('sources.toggleHint', '啟用 / 停用此來源')}
                className="size-4 accent-accent-700 shrink-0 cursor-pointer disabled:cursor-not-allowed"
            />
            {/* 點檔名 → 逐 chunk 預覽內文 */}
            <button
                type="button"
                onClick={onPreview}
                title={t('sources.viewTooltip')}
                className="flex items-center gap-2 min-w-0 flex-1 text-left cursor-pointer"
            >
                <FileText size={14} className="text-ink-400 shrink-0 group-hover:text-accent-700 transition" strokeWidth={1.5} />
                <span className={[
                    'text-sm truncate group-hover:underline',
                    disabled ? 'text-ink-400 line-through' : 'text-ink-900',
                ].join(' ')} title={fileName}>
                    {displayName}
                </span>
            </button>
            {chunkCount > 0 && (
                <span className="font-mono text-[10px] text-ink-400 shrink-0">
                    {chunkCount}
                </span>
            )}
        </div>
    );
}

function stripUploadPrefix(name) {
    if (!name) return '';
    return name.replace(/^\d{10,14}-[a-f0-9]{6,10}-/, '');
}
